import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { DateTime } from "luxon"
import { ErrorMessage, Notification, Text, TouchButton } from "pusatec-react-native"
import WizardStep from "../WizardStep"
import { WizardState } from "../use-wizard"
import MainScrollPanel from "../../../components/layout/MainScrollPanel"
import Base from "../../../components/layout/Base"
import Panel from "../../../components/layout/Panel"
import { BackIcon } from "../../../components/BackIcon"
import { useDeliveryCodeQuery, useSendDeliveryMutation } from "./queries.generated"
import { useUpdateDeliveryMutation } from "../../ViewDelivery/queries.generated"
import Loading from "../../../components/Loading"
import { Form } from "pusatec-react-native"
import { copyTextToClipboard } from "../../../utils/clipboard"
import { useTransientMessage } from "../../../utils/use_transient_message"

interface Props {
	wizardState: WizardState
	deliveryId: string
	onCancel: () => void
	onComplete: () => void
}

type ShareMode = "options" | "email"

export default function ShareDeliveryCodeStep(props: Props) {
	const { wizardState, deliveryId, onCancel, onComplete } = props
	const { t } = useTranslation()
	const [shareMode, setShareMode] = useState<ShareMode>("options")
	const [copyStatus, setCopyStatus] = useTransientMessage()
	const extensionTriggeredRef = useRef(false)
	const defaultUseCodeBefore = useMemo(
		() => DateTime.local().plus({ days: 7 }).toFormat("yyyy-MM-dd'T'HH:mm"),
		[]
	)

	const { data, loading, error, refetch } = useDeliveryCodeQuery({
		variables: { deliveryId },
		pollInterval: 2000,
	})
	const [updateDelivery, updateDeliveryM] = useUpdateDeliveryMutation()
	const [sendDelivery, sendDeliveryM] = useSendDeliveryMutation({
		onCompleted: () => onComplete(),
	})

	useEffect(() => {
		if (extensionTriggeredRef.current) {
			return
		}

		extensionTriggeredRef.current = true
		updateDelivery({
			variables: {
				input: {
					deliveryId,
					useCodeBefore: new Date(defaultUseCodeBefore).toISOString(),
				}
			}
		}).finally(() => {
			refetch()
		})
	}, [defaultUseCodeBefore, deliveryId, refetch, updateDelivery])

	if (loading || updateDeliveryM.loading) return <Loading />
	if (error || updateDeliveryM.error) return <ErrorMessage extra={error || updateDeliveryM.error} />

	const delivery = data?.delivery
	const deliveryCode = delivery?.deliveryCode ?? ""
	const useCodeBefore = delivery?.useDeliveryCodeBefore

	const handleCopy = async () => {
		if (!deliveryCode) {
			setCopyStatus(t("Copy failed"))
			return
		}

		const copied = await copyTextToClipboard(deliveryCode)
		setCopyStatus(copied ? t("Code copied") : t("Copy failed"))
	}

	if (shareMode === "email") {
		return (
			<WizardStep title={t("")} wizardState={wizardState} scroll>
				<div style={{ fontFamily: "Nunito, Mada-Regular, sans-serif", fontSize: 25, textAlign: "center" }}>
					{t("Send delivery code")}
				</div>
				{deliveryCode ? (
					<Base style={{ marginBottom: 15 }}>
						<Text text={t("Delivery code")} />
						<Text text={deliveryCode} style={{ textAlign: "center", fontSize: 32, fontWeight: "bold" }} />
						{useCodeBefore ? <Text text={t("Code valid until {{time}}", { time: DateTime.fromISO(useCodeBefore).toFormat("dd.MM.yyyy HH:mm") })} /> : null}
					</Base>
				) : null}
				<Form<{ email: string }>
					style={{ flex: 1, width: "100%", display: "flex" }}
					initialValues={{ email: "" }}
					onSubmit={(values) => {
						sendDelivery({
							variables: {
								input: {
									deliveryId,
									email: values.email.trim(),
									useCodeBefore: useCodeBefore ?? new Date(defaultUseCodeBefore).toISOString(),
								}
							}
						})
					}}
				>
					<MainScrollPanel>
						<Base style={{ paddingTop: 30 }}>
							<Form.TextField
								autoFocus
								inputStyle={{ fontSize: 18 }}
								required
								name="email"
								label={t("Recipient email")}
								validate={value => {
									if (!value) return t("Required")
									const trimmed = value.trim()
									if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return t("Invalid email")
								}}
							/>
							{sendDeliveryM.error ? <ErrorMessage extra={sendDeliveryM.error} /> : null}
						</Base>
					</MainScrollPanel>
					<Panel style={{ flexDirection: "row", justifyContent: "space-between" }}>
						<TouchButton
							type="secondary"
							text={t("Go back")}
							icon={BackIcon}
							onPress={() => setShareMode("options")}
						/>
						<Form.Submit type="TouchButton" text={t("Send")} loading={sendDeliveryM.loading} />
					</Panel>
				</Form>
			</WizardStep>
		)
	}

	return (
		<WizardStep title={t("")} wizardState={wizardState} scroll>
			<div style={{ fontFamily: "Nunito, Mada-Regular, sans-serif", fontSize: 25, textAlign: "center" }}>
				{t("Share delivery code")}
			</div>
			<MainScrollPanel>
				<Base>
					<Text text={t("Delivery code")} style={{ textAlign: "center" }} />
					<Text text={deliveryCode} style={{ textAlign: "center", fontSize: 42, fontWeight: "bold" }} />
					{useCodeBefore ? (
						<Text
							text={t("Code valid until {{time}}", {
								time: DateTime.fromISO(useCodeBefore).toFormat("dd.MM.yyyy HH:mm"),
							})}
							style={{ textAlign: "center" }}
						/>
					) : null}
				</Base>
				{copyStatus ? <Notification type="success" text={copyStatus} containerStyle={{ marginTop: 10 }} /> : null}
				<Base style={{ marginTop: 10 }}>
					<TouchButton
						type="primary"
						text={t("Send by email")}
						onPress={() => setShareMode("email")}
					/>
					<TouchButton
						type="secondary"
						text={t("Copy code")}
						onPress={handleCopy}
						containerStyle={{ marginTop: 10 }}
					/>
				</Base>
			</MainScrollPanel>
			<Panel style={{ flexDirection: "row", justifyContent: "space-between" }}>
				<TouchButton
					type="secondary"
					text={t("Go back")}
					icon={BackIcon}
					onPress={onCancel}
				/>
				<TouchButton
					type="primary"
					text={t("Ready")}
					onPress={onComplete}
				/>
			</Panel>
		</WizardStep>
	)
}
